import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import { createBrainstorm, createAssetKnowledgeBase } from "../../../services/Brainstorm/BrainstormService";
import Swal from "sweetalert2";
import AnimatedBrainLoader from "../../Atoms/AnimatedBrainLoader";
import Container from "../../Atoms/Container";
import ConceptCard from "./Molecules/ConceptCard";
import ConceptDetailsModal from "./Molecules/ConceptDetailsModal";
import useBrainstormStore from "../../../hooks/useBrainstormStore";

import './styles.css';
import ButtonMain from "../../Atoms/ButtonMain";
import KnowledgeBaseModal from "../BrainstormIdeasEdit/Molecules/KnowledgeBaseModal";

export default function BrainstormConceptsEditTemplate({ data, loadingData, hasError }) {
    const { projectId, outputId } = useParams();  
    const { keycloak } = useKeycloak();
    const navigate = useNavigate();
    const KEYCLOACK_TOKEN = keycloak.token;

    const [selectedGuidingQuestion, setSelectedGuidingQuestion] = useState(null);
    const [currentBrainstorm, setCurrentBrainstorm] = useState(null);
    const [guidingQuestions, setGuidingQuestions] = useState([]);
    const [selectedIdeas, setSelectedIdeas] = useState([]);
    const [loadingKnowledgeBase, setLoadingKnowledgeBase] = useState(false);
    const [selectedIdea, setSelectedIdea] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showKnowledgeBaseReviewModal, setShowKnowledgeBaseReviewModal] = useState(false);
    const { getBrainstormById } = useBrainstormStore();

    // Helper function to get guiding questions array (handle both object and array)
    const getGuidingQuestionsArray = useCallback((brainstorm) => {
        if (!brainstorm || !brainstorm.guiding_questions) return [];
        return Array.isArray(brainstorm.guiding_questions) 
            ? brainstorm.guiding_questions 
            : Object.values(brainstorm.guiding_questions);
    }, []);

    const getIdeasByGuidingQuestionId = useCallback((questionId) => {
        const sourceData = currentBrainstorm || data;
        if (!sourceData) return [];

        const guidingQuestionsArr = getGuidingQuestionsArray(sourceData);
        const question = guidingQuestionsArr.find(q => q.id === questionId);

        return question?.ideas || [];
    }, [data, currentBrainstorm, getGuidingQuestionsArray]);

    // eslint-disable-next-line no-unused-vars
    const getGuidingQuestionsByChallengeId = useCallback((challengeId) => {
        if (!currentBrainstorm) return [];
        const guidingQuestionsArr = getGuidingQuestionsArray(currentBrainstorm);
        return guidingQuestionsArr.filter(q => q.challenge_id === challengeId);
    }, [currentBrainstorm, getGuidingQuestionsArray]);

    const getIdeaById = useCallback((ideaId) => {
        if (!currentBrainstorm) return null;
        const guidingQuestionsArr = getGuidingQuestionsArray(currentBrainstorm);
        for (const question of guidingQuestionsArr) {
            const idea = question.ideas?.find(i => i.id === ideaId);
            if (idea) return idea;
        }
        return null;
    }, [currentBrainstorm, getGuidingQuestionsArray]);

    // eslint-disable-next-line no-unused-vars
    const getChallengeById = useCallback((challengeId) => {
        if (!currentBrainstorm) return null;
        const challenges = Array.isArray(currentBrainstorm.challenges) 
            ? currentBrainstorm.challenges 
            : Object.values(currentBrainstorm.challenges || {});
        return challenges.find(c => c.id === challengeId) || null;
    }, [currentBrainstorm]);

    useEffect(() => {
        if (!data) {
            setCurrentBrainstorm(null);
            setGuidingQuestions([]);
            setSelectedIdeas([]);
            setSelectedGuidingQuestion(null);
            return;
        }

        setCurrentBrainstorm(data);
        
        const selectedChallenge = (Array.isArray(data.challenges) 
            ? data.challenges 
            : Object.values(data.challenges || {})).find(challenge => challenge.selected === true);

        if (!selectedChallenge) {
            console.warn('No selected challenge found');
            setGuidingQuestions([]);
            setSelectedIdeas([]);
            setSelectedGuidingQuestion(null);
            return;
        }

        const guidingQuestionsArr = getGuidingQuestionsArray(data);
        const challengeGuidingQuestions = guidingQuestionsArr.filter(
            question => question.challenge_id === selectedChallenge.id
        );
        const selectedGuidingQuestions = challengeGuidingQuestions.filter(
            question => question.selected === true
        );
        setGuidingQuestions(selectedGuidingQuestions);
        
        if (selectedGuidingQuestions.length === 0) {
            console.warn('No selected guiding questions found');
            setSelectedIdeas([]);
            setSelectedGuidingQuestion(null);
            return;
        }

        // Set first question as selected if current selection is invalid or not set
        const currentQuestionValid = selectedGuidingQuestions.some(q => q.id === selectedGuidingQuestion);
        if (!currentQuestionValid && selectedGuidingQuestions.length > 0) {
            const firstQuestion = selectedGuidingQuestions[0];
            setSelectedGuidingQuestion(firstQuestion.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, getGuidingQuestionsArray]);

    // Update selected ideas when guiding question changes
    useEffect(() => {
        if (!selectedGuidingQuestion || (!data && !currentBrainstorm)) {
            setSelectedIdeas([]);
            return;
        }

        const allIdeas = getIdeasByGuidingQuestionId(selectedGuidingQuestion);
        const allIdeasArr = Object.values(allIdeas || {});
        
        const selected = allIdeasArr.filter(idea => idea.selected === true);
        setSelectedIdeas(selected);
    }, [selectedGuidingQuestion, data, currentBrainstorm, getIdeasByGuidingQuestionId]);

    const handleGuidingQuestionSelect = (question) => {
        if (!currentBrainstorm) return;
        
        setSelectedGuidingQuestion(question.id);
    };

    const handleConceptCardClick = (idea) => {
        if (idea?.id) {
            const latestIdea = getIdeaById(idea.id);
            setSelectedIdea(latestIdea || idea);
        } else {
            setSelectedIdea(idea);
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedIdea(null);
    };

    const handleConceptDeleted = (ideaId) => {
        if (!currentBrainstorm) return;

        // Update the brainstorm data by removing the concept from the idea
        const guidingQuestionsArr = getGuidingQuestionsArray(currentBrainstorm);
        const updatedGuidingQuestions = guidingQuestionsArr.map(question => ({
            ...question,
            ideas: question.ideas?.map(idea => {
                if (idea.id === ideaId) {
                    // Remove the concept property from the idea
                    const { concept, ...ideaWithoutConcept } = idea;
                    return ideaWithoutConcept;
                }
                return idea;
            }) || []
        }));

        const updatedBrainstorm = {
            ...currentBrainstorm,
            guiding_questions: updatedGuidingQuestions,
            updatedAt: new Date().toISOString()
        };

        setCurrentBrainstorm(updatedBrainstorm);

        // Update selectedIdeas to reflect the change
        if (selectedGuidingQuestion) {
            const question = updatedGuidingQuestions.find(q => q.id === selectedGuidingQuestion);
            const allIdeas = question?.ideas || [];
            const selected = allIdeas.filter(idea => idea.selected === true);
            setSelectedIdeas(selected);
        }

        // Close the modal if the deleted idea was the selected one
        if (selectedIdea?.id === ideaId) {
            setShowModal(false);
            setSelectedIdea(null);
        }
    };

    const handleConceptUpdated = (ideaId, updatedConcept) => {
        if (!currentBrainstorm) return;

        const applyUpdate = (brainstorm) => {
            const guidingQuestionsArr = getGuidingQuestionsArray(brainstorm);
            const updatedGuidingQuestions = guidingQuestionsArr.map(question => ({
                ...question,
                ideas: question.ideas?.map(idea => {
                    if (idea.id === ideaId && updatedConcept) {
                        return { ...idea, concept: updatedConcept };
                    }
                    return idea;
                }) || []
            }));

            return {
                ...brainstorm,
                guiding_questions: updatedGuidingQuestions,
                updatedAt: new Date().toISOString()
            };
        };

        let baseBrainstorm = currentBrainstorm;
        if (currentBrainstorm.id) {
            const storeBrainstorm = getBrainstormById(currentBrainstorm.id);
            if (storeBrainstorm) {
                baseBrainstorm = storeBrainstorm;
            }
        }

        const updatedBrainstorm = applyUpdate(baseBrainstorm);
        setCurrentBrainstorm(updatedBrainstorm);

        if (selectedGuidingQuestion) {
            const guidingQuestionsArr = getGuidingQuestionsArray(updatedBrainstorm);
            const question = guidingQuestionsArr.find(q => q.id === selectedGuidingQuestion);
            const allIdeas = question?.ideas || [];
            const selected = allIdeas.filter(idea => idea.selected === true);
            setSelectedIdeas(selected);

            if (selectedIdea?.id === ideaId) {
                const updatedIdea = allIdeas.find(idea => idea.id === ideaId);
                if (updatedIdea) {
                    setSelectedIdea(updatedIdea);
                }
            }
        }
    };

    const handleFinishBrainstorm = () => {
        setShowKnowledgeBaseReviewModal(true);
    }

    const generateNewFileName = () => {
        const date = new Date();
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const seconds = date.getSeconds();

        return 'brainstorm_' + year + month + day + '_' + hours + minutes + seconds;
    }

    const handleToggleKnowledgeBase = (outputId, questionId) => {
        if (!currentBrainstorm) return;
        
        const guidingQuestionsArr = getGuidingQuestionsArray(currentBrainstorm);
        const updatedGuidingQuestions = guidingQuestionsArr.map(question => {
            if (question.id === questionId) {
                return { ...question, in_knowledge_base: !question.in_knowledge_base };
            }
            return question;
        });

        const updatedBrainstorm = {
            ...currentBrainstorm,
            guiding_questions: updatedGuidingQuestions
        };

        setCurrentBrainstorm(updatedBrainstorm);
        
        // Update guidingQuestions state if needed
        const selectedChallenge = (Array.isArray(updatedBrainstorm.challenges) 
            ? updatedBrainstorm.challenges 
            : Object.values(updatedBrainstorm.challenges || {})).find(c => c.selected === true);
        
        if (selectedChallenge) {
            const challengeGuidingQuestions = updatedGuidingQuestions.filter(
                question => question.challenge_id === selectedChallenge.id
            );
            const selectedGuidingQuestions = challengeGuidingQuestions.filter(
                question => question.selected === true
            );
            setGuidingQuestions(selectedGuidingQuestions);
        }
    }

    const handleFinishKnowledgeBaseReview = async (brainstormData) => {
        try {
            setLoadingKnowledgeBase(true);
            
            const brainstormForPayload = brainstormData || currentBrainstorm;
            if (!brainstormForPayload) {
                Swal.fire({
                    icon: 'error',
                    title: 'Erro ao finalizar trabalho',
                    text: 'Erro ao buscar dados atualizados. Tente novamente.',
                    timer: 2000,
                    showConfirmButton: false,
                    background: '#27272A',
                    color: '#fff',
                    position: 'top-end',
                });
                return;
            }
            
            const guidingQuestionsArr = getGuidingQuestionsArray(brainstormForPayload);
            const allBrainstormIdeas = guidingQuestionsArr.flatMap(question => question.ideas || []);
            
            const payload = {
                challenges: Array.isArray(brainstormForPayload.challenges) 
                    ? brainstormForPayload.challenges 
                    : Object.values(brainstormForPayload.challenges || {}),
                guidingQuestions: guidingQuestionsArr.reduce((acc, q) => {
                    acc[q.id] = q;
                    return acc;
                }, {}),
                ideas: allBrainstormIdeas.reduce((acc, idea) => {
                    acc[idea.id] = idea;
                    return acc;
                }, {}),
                groups: Array.isArray(brainstormForPayload.groups)
                    ? brainstormForPayload.groups.reduce((acc, g) => { acc[g.id] = g; return acc; }, {})
                    : (brainstormForPayload.groups || {}),
                concepts: Array.isArray(brainstormForPayload.concepts)
                    ? brainstormForPayload.concepts.reduce((acc, c) => { acc[c.id] = c; return acc; }, {})
                    : (brainstormForPayload.concepts || {})
            }

            const newBrainstorm = await createBrainstorm(projectId, payload, KEYCLOACK_TOKEN);

            if (newBrainstorm) {
                const challenges = Array.isArray(brainstormForPayload.challenges) 
                    ? brainstormForPayload.challenges 
                    : Object.values(brainstormForPayload.challenges || {});
                const currentChallenge = challenges.find(c => c.selected === true);
                
                if (!currentChallenge) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Erro ao finalizar trabalho',
                        text: 'Erro ao identificar desafio principal. Tente novamente.',
                    });
                    return;
                }

                const currentGuidingQuestion = guidingQuestions.find(q => q.id === selectedGuidingQuestion);
                if (!currentGuidingQuestion) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Erro ao finalizar trabalho',
                        text: 'Erro ao identificar questão norteadora. Tente novamente.',
                    });
                    return;
                }

                const formattedGuidingQuestions = guidingQuestionsArr
                    .filter(question => question.in_knowledge_base && question.ideas?.some(idea => idea.selected))
                    .map(q => ({
                        ...q,
                        ideas: q.ideas?.filter(idea => idea.selected) || []
                    }));

                const newFileName = generateNewFileName();

                const assetPayload = {
                    templateName: 'brainstorm',
                    challenge: currentChallenge.text,
                    blocks: formattedGuidingQuestions,
                    fileName: newFileName
                }

                const assetKnowledgeBase = await createAssetKnowledgeBase(projectId, assetPayload, newFileName, KEYCLOACK_TOKEN);
                if (assetKnowledgeBase) {
                    Swal.fire({
                        title: 'Trabalho finalizado!',
                        text: 'O trabalho foi finalizado com sucesso.',
                        timer: 1500,
                        showConfirmButton: false,
                        background: '#27272A',
                        color: '#fff',
                        position: 'top-end',
                    });
        
                    navigate(`/project/${projectId}`);
                } else {
                    Swal.fire({
                        title: 'Erro ao criar asset da base de conhecimento',
                        text: 'Ocorreu um erro ao criar o asset da base de conhecimento. Tente novamente.',
                        timer: 2000,
                        showConfirmButton: false,
                        background: '#27272A',
                        color: '#fff',
                        position: 'top-end',
                    });
                    return;
                }
            } else {
                Swal.fire({
                    title: 'Erro ao finalizar trabalho',
                    text: 'Ocorreu um erro ao concluir a finalização do trabalho. Tente novamente.',
                    timer: 2000,
                    showConfirmButton: false,
                    background: '#27272A',
                    color: '#fff',
                    position: 'top-end',
                });
                return;
            }
        } catch (error) {
            console.error('Error finishing knowledge base review:', error);
            Swal.fire({
                title: 'Erro ao finalizar trabalho',
                text: 'Ocorreu um erro ao concluir a revisão da base de conhecimento. Tente novamente.',
                timer: 2000,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
        } finally {
            setLoadingKnowledgeBase(false);
            setShowKnowledgeBaseReviewModal(false);
        }
    };

    return (
        <>
            <Container>
                <section id="brainstorm-concepts-header" className="mb-3">
                    <div className="d-flex flex-row align-items-center justify-content-between">
                        <h3>Conceitos gerados</h3>

                        <ButtonMain onClick={() => handleFinishBrainstorm()} className="btn btn-primary">
                            Finalizar
                        </ButtonMain>
                    </div>
                </section>
            </Container>

            <div className="brainstorm-concepts-header" />

            <section id="brainstorm-concepts-content">
                <Container>
                    <div className="row brainstorm-concepts-layout">
                        {/* Left Column - Guiding Questions */}
                        <div className="col-12 col-lg-4 brainstorm-concepts-left-column">
                            <div className="brainstorm-concepts-scrollable-content">
                                {loadingData ? (
                                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                                        <AnimatedBrainLoader size={100} />
                                    </div>
                                ) : (
                                    guidingQuestions.map((question, index) => {
                                        const questionIdeas = getIdeasByGuidingQuestionId(question.id) || [];
                                        const selectedCount = questionIdeas.filter(idea => idea.selected === true).length;
                                        
                                        return (
                                            <div 
                                                key={question.id} 
                                                className={`problem-statement-item ${selectedGuidingQuestion === question.id ? 'selected' : ''}`}
                                                onClick={() => handleGuidingQuestionSelect(question)}
                                                role="button"
                                                tabIndex={0}
                                                aria-pressed={selectedGuidingQuestion === question.id}
                                            >
                                                <p>{question.text}</p>
                                                {selectedCount > 0 && (
                                                    <span className="selected-ideas-count">{selectedCount} conceito(s)</span>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        {/* Right Column - Concept Cards */}
                        <div className="col-12 col-lg-8 brainstorm-concepts-right-column">
                            <div className="brainstorm-concepts-scrollable-content">
                                {loadingData ? (
                                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                                        <div className="d-flex flex-column align-items-center justify-content-center">
                                            <AnimatedBrainLoader size={200} />
                                            <p className="text-secondary mt-3">Carregando conceitos...</p>
                                        </div>
                                    </div>
                                ) : (
                                    !hasError && (() => {
                                        const ideasWithConcepts = selectedIdeas.filter(idea => idea.concept);
                                        console.log('ideasWithConcepts ===>', ideasWithConcepts);
                                        
                                        if (ideasWithConcepts.length === 0) {
                                            return (
                                                <div className="empty-state">
                                                    <p className="text-secondary">Nenhum conceito gerado para esta questão norteadora.</p>
                                                </div>
                                            );
                                        }
                                        
                                        return (
                                            <div className="concepts-grid">
                                                {ideasWithConcepts.map((idea) => (
                                                    <ConceptCard
                                                        key={idea.id}
                                                        id={idea.id}
                                                        ideaId={idea.id}
                                                        title={idea.concept?.titulo_do_conceito || "Nome do conceito"}
                                                        description={idea.concept?.descricao_do_conceito || "Descrição do conceito"}
                                                        conceptImageUrl={idea.concept?.signed_image || null}
                                                        onClick={() => handleConceptCardClick(idea)}
                                                    />
                                                ))}
                                            </div>
                                        );
                                    })()
                                )}
                            </div>
                        </div>
                    </div>
                </Container>
            </section>

            <ConceptDetailsModal
                show={showModal}
                onHide={handleCloseModal}
                idea={selectedIdea}
                brainstormData={currentBrainstorm}
                onConceptDeleted={handleConceptDeleted}
                onConceptUpdated={handleConceptUpdated}
            />

            <KnowledgeBaseModal
                show={showKnowledgeBaseReviewModal}
                onHide={() => setShowKnowledgeBaseReviewModal(false)}
                outputId={outputId}
                onToggleKnowledgeBase={handleToggleKnowledgeBase}
                handleConclude={handleFinishKnowledgeBaseReview}
                loading={loadingData || loadingKnowledgeBase}
                brainstormData={currentBrainstorm}
            />
        </>
    );
}
